import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import {
  ArrowLeft,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  Users,
  Send,
  SkipForward,
  Ban,
  MessageSquare,
  Calendar,
  Activity,
} from 'lucide-react';

interface ReminderRun {
  id: number;
  site_id: number | null;
  policy_id: number | null;
  run_type: 'scheduled' | 'manual';
  status: 'running' | 'queued' | 'completed' | 'failed';
  started_at: string;
  finished_at: string | null;
  total_candidates: number;
  total_queued: number;
  total_sent: number;
  total_skipped: number;
  total_failed: number;
  error_message: string | null;
  metadata: Record<string, unknown>;
}

interface Attempt {
  id: number;
  status: string;
  message_type: string;
  skip_reason: string | null;
  amount: number;
  due_date: string;
  sent_at: string | null;
  failed_at: string | null;
  error_message: string | null;
  subscriber_name: string;
  mobile_no_normalized: string;
  invoice_number: string;
}

interface Stats {
  attempt_breakdown: Record<string, number>;
  message_type_breakdown: Record<string, number>;
  skip_reason_breakdown: Record<string, number>;
}

export function ReminderRunDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [run, setRun] = useState<ReminderRun | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [attempts, setAttempts] = useState<Attempt[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchData = async () => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/reminders/runs/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      if (response.data.success) {
        setRun(response.data.data.run);
        setStats(response.data.data.stats);
        setAttempts(response.data.data.sample_attempts);
      }
    } catch (error) {
      toast.error('Failed to load reminder run details');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchData();
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<
      string,
      { color: string; icon: React.ReactNode }
    > = {
      running: { color: 'bg-blue-100 text-blue-800', icon: <RefreshCw className="w-4 h-4" /> },
      queued: { color: 'bg-yellow-100 text-yellow-800', icon: <Clock className="w-4 h-4" /> },
      completed: { color: 'bg-green-100 text-green-800', icon: <CheckCircle className="w-4 h-4" /> },
      failed: { color: 'bg-red-100 text-red-800', icon: <XCircle className="w-4 h-4" /> },
    };

    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', icon: null };

    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        {config.icon}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getAttemptStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
      case 'delivered':
      case 'read':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'skipped':
        return <SkipForward className="w-4 h-4 text-yellow-500" />;
      case 'cancelled':
        return <Ban className="w-4 h-4 text-gray-500" />;
      default:
        return <Clock className="w-4 h-4 text-blue-500" />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!run) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <AlertCircle className="w-6 h-6 text-red-600 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-red-900">Run Not Found</h3>
              <p className="text-sm text-red-700 mt-1">
                The reminder run you're looking for doesn't exist or you don't have access.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const successRate = run.total_candidates > 0
    ? ((run.total_sent / run.total_candidates) * 100).toFixed(1)
    : '0';

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/reminders')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Reminder Run #{run.id}</h1>
            <p className="text-sm text-gray-500 mt-1">
              {getStatusBadge(run.status)}
              <span className="ml-3">
                {run.run_type === 'scheduled' ? 'Scheduled' : 'Manual'} Run
              </span>
              {run.policy_id && (
                <span className="ml-3 text-blue-600">Policy #{run.policy_id}</span>
              )}
            </p>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Candidates"
          value={run.total_candidates}
          icon={<Users className="w-5 h-5" />}
          color="bg-blue-50 text-blue-600"
        />
        <StatCard
          title="Reminders Sent"
          value={run.total_sent}
          icon={<Send className="w-5 h-5" />}
          color="bg-green-50 text-green-600"
          subtitle={`${successRate}% success rate`}
        />
        <StatCard
          title="Skipped"
          value={run.total_skipped}
          icon={<SkipForward className="w-5 h-5" />}
          color="bg-yellow-50 text-yellow-600"
        />
        <StatCard
          title="Failed"
          value={run.total_failed}
          icon={<XCircle className="w-5 h-5" />}
          color="bg-red-50 text-red-600"
        />
      </div>

      {/* Run Timeline */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Run Timeline</h3>
        <div className="flex items-center gap-8">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500">Started</p>
              <p className="font-medium">
                {run.started_at ? format(new Date(run.started_at), 'PPp') : 'N/A'}
              </p>
            </div>
          </div>
          {run.finished_at && (
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Finished</p>
                <p className="font-medium">
                  {format(new Date(run.finished_at), 'PPp')}
                </p>
              </div>
            </div>
          )}
          {run.finished_at && run.started_at && (
            <div className="flex items-center gap-3">
              <Activity className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Duration</p>
                <p className="font-medium">
                  {Math.round(
                    (new Date(run.finished_at).getTime() -
                      new Date(run.started_at).getTime()) /
                      1000
                  )}{' '}
                  seconds
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Breakdown Stats */}
      {stats && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Attempt Breakdown */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Status Breakdown</h3>
            <div className="space-y-2">
              {Object.entries(stats.attempt_breakdown).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    {getAttemptStatusIcon(status)}
                    <span className="capitalize">{status}</span>
                  </span>
                  <span className="font-medium">{count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Message Types */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Message Types</h3>
            <div className="space-y-2">
              {Object.entries(stats.message_type_breakdown).map(([type, count]) => (
                <div key={type} className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-gray-400" />
                    <span className="capitalize">{type.replace('_', ' ')}</span>
                  </span>
                  <span className="font-medium">{count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Skip Reasons */}
          {Object.keys(stats.skip_reason_breakdown).length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Skip Reasons</h3>
              <div className="space-y-2">
                {Object.entries(stats.skip_reason_breakdown).map(([reason, count]) => (
                  <div key={reason} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">{reason}</span>
                    <span className="font-medium">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {run.error_message && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 mr-3 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-red-900">Error</h4>
              <p className="text-sm text-red-700 mt-1">{run.error_message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Metadata */}
      {run.metadata && Object.keys(run.metadata).length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Metadata</h3>
          <pre className="bg-gray-50 rounded p-4 text-sm overflow-auto">
            {JSON.stringify(run.metadata, null, 2)}
          </pre>
        </div>
      )}

      {/* Recent Attempts */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-semibold">Recent Attempts</h3>
          <p className="text-sm text-gray-500 mt-1">
            Showing last {attempts.length} attempts
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Subscriber
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Invoice
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Sent At
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {attempts.map((attempt) => (
                <tr key={attempt.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {getAttemptStatusIcon(attempt.status)}
                      <span className="capitalize text-sm">{attempt.status}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-sm">{attempt.subscriber_name}</p>
                      <p className="text-sm text-gray-500">{attempt.mobile_no_normalized}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm">{attempt.invoice_number}</td>
                  <td className="px-6 py-4 text-sm">
                    ₹{attempt.amount?.toFixed(2) || '0.00'}
                  </td>
                  <td className="px-6 py-4">
                    <span className="capitalize text-sm">
                      {attempt.message_type?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {attempt.sent_at
                      ? format(new Date(attempt.sent_at), 'PPp')
                      : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatCard({
  title,
  value,
  icon,
  color,
  subtitle,
}: {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
}) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>{icon}</div>
      </div>
    </div>
  );
}
